import { useRouter, type NextRouter } from 'next/router';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { filterDynamicRouteParams, getDynamicRouteParams } from './utils/routeUtils';

const splitQueryForPath = (query: Record<string, unknown>, pathname: string) => {
  const dynamicParams = getDynamicRouteParams(pathname);
  const dynamicQuery = Object.fromEntries(
    dynamicParams.map((key) => [key, query[key]]).filter(([, value]) => value !== undefined),
  );
  const filteredQuery = filterDynamicRouteParams(query, pathname);

  return { dynamicQuery, filteredQuery };
};

type QueryParamRouter = Pick<NextRouter, 'pathname' | 'query' | 'replace'>;

export const createSetQueryParams =
  (getRouter: () => QueryParamRouter) => (newValues: Record<string, string | undefined>) => {
    const router = getRouter();
    const { dynamicQuery, filteredQuery } = splitQueryForPath(router.query, router.pathname);
    const updatedQuery = { ...filteredQuery };

    Object.entries(newValues).forEach(([param, value]) => {
      if (!value) {
        delete updatedQuery[param];
      } else {
        updatedQuery[param] = value;
      }
    });

    return router.replace(
      {
        pathname: router.pathname,
        query: {
          ...dynamicQuery,
          ...updatedQuery,
        },
      },
      undefined,
      { shallow: true },
    );
  };

// Does not store history, use router.push here if you want that
export const useQueryParam = (param: string, initialValue?: string) => {
  const router = useRouter();

  const setQueryParam = useCallback(
    (newVal?: string | null) => {
      const { dynamicQuery, filteredQuery } = splitQueryForPath(router.query, router.pathname);
      if (!newVal) {
        delete filteredQuery[param];
      }
      router
        .replace(
          {
            pathname: router.pathname,
            query: {
              ...dynamicQuery,
              ...filteredQuery,
              ...(newVal ? { [param]: newVal } : {}),
            },
          },
          undefined,
          { shallow: true },
        )
        .catch(() => undefined);
    },
    // oxlint-disable-next-line react-hooks/exhaustive-deps
    // Do not include router here or you get infinite re-renders (even router.query)
    [param, JSON.stringify(router.query)],
  );

  useEffect(() => {
    if (!router.isReady) {
      return;
    }
    if (router.query[param] === undefined && initialValue) {
      setQueryParam(initialValue);
    }
  }, [initialValue, param, router.isReady, router.query, setQueryParam]);

  return [router.query[param] as string, setQueryParam] as const;
};

export const useQueryParams = (params: string[]) => {
  const router = useRouter();

  // Keep a ref to the current router so the stable setQueryParams callback
  // always reads the latest query/pathname without needing router in its deps
  // (adding router to deps causes infinite re-renders).
  const routerRef = useRef(router);
  routerRef.current = router;

  // we only need to support one value per param for now
  const currentValues = useMemo(
    () =>
      Object.fromEntries(
        params.map((param) => [param, typeof router.query[param] === 'string' ? router.query[param] : '']),
      ),
    [params, router.query],
  );

  const setQueryParams = useCallback((newValues: Record<string, string | undefined>) => {
    createSetQueryParams(() => routerRef.current)(newValues).catch(() => undefined);
  }, []);

  return [currentValues, setQueryParams] as const;
};

// Convience for numeric query params
export const useQueryParamNumeric = (param: string, initialValue: number) => {
  const [queryParamStr, setQueryParamStr] = useQueryParam(param, initialValue?.toString());

  return [
    queryParamStr ?
      isNaN(Number(queryParamStr)) ? initialValue
      : Number(queryParamStr)
    : initialValue,
    (num?: number) => setQueryParamStr(num?.toString()),
  ] as const;
};
